import { Modal, SubmitButton } from "@xola/ui-kit";
import React from "react";
import { useMutation } from "react-query";
import { LineItemObject, LineItemType, Purchase, PurchaseStatus } from "types";
import { api } from "../../api/api";
import {
    CreatePurchaseItemLineItem,
    CreatePurchaseLineItem,
    useCreatePurchaseStore,
} from "../../stores/createPurchaseStore";
import { numberFormat } from "../../utils/numbers";
import { SecondaryButton } from "../Common/SecondaryButton";
import { useSeller } from "@/hooks/useAccount";

interface RemoveCouponConfirmModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const RemoveCouponMessage = () => {
    const { currency } = useSeller();
    return (
        <span>
            A coupon worth {numberFormat(0, currency)} is applied to this purchase. This must be removed in order to
            complete this purchase.
            <br />
            <br />
            You will have a chance to review this purchase before it is placed.{" "}
        </span>
    );
};

interface RemoveCouponData {
    path: string;
    op: string;
    data: { id: string; object: LineItemObject };
}

export const RemoveCouponConfirmModal = ({ isOpen, onClose }: RemoveCouponConfirmModalProps) => {
    const setPurchase = useCreatePurchaseStore((state) => state.actions.setPurchase);

    const { mutateAsync: removeCouponCode, isLoading: isRemovingCouponCode } = useMutation(async () => {
        const { purchase, purchaseId } = useCreatePurchaseStore.getState();
        const ops: RemoveCouponData[] = [];
        for (const item of purchase.items) {
            for (const lineItem of item.lineItems) {
                if (
                    !!lineItem.id &&
                    "type" in lineItem &&
                    lineItem.type === LineItemType.COUPON_DISCOUNT &&
                    lineItem?.amount === 0
                ) {
                    ops.push({
                        path: `/items/${item.id}/lineItems`,
                        op: "removeLineItem",
                        data: { id: lineItem.id, object: LineItemObject.COUPON },
                    });
                }
            }
        }

        const payload = { ops };
        const { data } = await api.patch<Purchase>(`/purchases/${purchaseId}?validationLevel=info`, payload);
        if (purchase.status === PurchaseStatus.DRAFT) {
            setPurchase((draft) => {
                const clonedItems = draft.items.map((item, index) => {
                    item.lineItems = data.items[index].lineItems as CreatePurchaseItemLineItem[];
                    return { ...item };
                });
                draft.items = clonedItems;
                draft.lineItems = data.lineItems as CreatePurchaseLineItem[];
            });
        }
    });

    const handleSubmit = async () => {
        try {
            await removeCouponCode();
            onClose();
        } catch {
            // If there is any error in coupon applying, don't close the modal
        }
    };

    return (
        <Modal size="small" isOpen={isOpen} className="p-6" onClose={onClose}>
            <Modal.Header>Remove Coupon</Modal.Header>
            <Modal.Body>
                <div className="pl-5">
                    <RemoveCouponMessage />
                </div>
            </Modal.Body>
            <Modal.Footer className="space-x-4 border-t border-gray-light pt-3.5">
                <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
                <SubmitButton isLoading={isRemovingCouponCode} color="primary" onClick={handleSubmit}>
                    Remove Coupon
                </SubmitButton>
            </Modal.Footer>
        </Modal>
    );
};
